import React from 'react';
import PropTypes from 'prop-types';
import { CheckDiffText } from '../../../common/utils';

const StevedoringData = ({ serviceProvider }) => {
    const shouldMap = React.useMemo(() => {
        const newLim = serviceProvider.provider && serviceProvider.provider.limitations || [];
        const oldLim = serviceProvider.limitations || [];
        if (newLim.length > oldLim.length) {
            return newLim;
        }
        return oldLim;
    }, [serviceProvider]);

    const renderNormalView = () => (
        <div className="paper-block" style={{ backgroundColor: 'inherit' }}>
            <div className='title'>{serviceProvider.activities.join(',')}</div>
            {serviceProvider.limitations.map((limitation, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span style={{ wordBreak: 'break-all', display: 'inline' }}>{limitation.berth}</span>
                    </div>
                    <div className="limitation">
                        Max draft <span>{limitation.maxDraught} m</span>
                    </div>
                    <div className="limitation">
                        Max beam <span>{limitation.maxBeam} m</span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>{limitation.maxLOA} m</span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>{limitation.maxDWT} m</span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>{limitation.channelDepth} m</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRemoveView = () => (
        <div className="paper-block">
            <div className='title'>
                <CheckDiffText
                    oldValue={serviceProvider.activities.join(',')}
                    newValue={null}
                />
            </div>
            {serviceProvider.limitations.map((limitation, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span>
                            <CheckDiffText
                                oldValue={limitation.berth}
                                newValue={null}
                            />
                        </span>
                    </div>
                    <div className="limitation">
                        Max draft <span>
                            <CheckDiffText
                                oldValue={limitation.maxDraught}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max beam <span>
                            <CheckDiffText
                                oldValue={limitation.maxBeam}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>
                            <CheckDiffText
                                oldValue={limitation.maxLOA}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>
                            <CheckDiffText
                                oldValue={limitation.maxDWT}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>
                            <CheckDiffText
                                oldValue={limitation.channelDepth}
                                newValue={null}
                            /> m
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderChangeView = () => {
        const p = serviceProvider.provider || {};
        const old = serviceProvider;
        const newLim = p.limitations || old.limitations || [];
        const oldLim = old.limitations || [];

        return (
        <div className="paper-block" style={{ backgroundColor: 'inherit' }}>
            <div className='title'>
                <CheckDiffText
                    oldValue={(old.activities || []).join(',')}
                    newValue={(p.activities || old.activities || []).join(',')}
                />
            </div>
            {shouldMap.map((_, index) => (
                <div 
                    className="limitations"
                    key={`Limitation: ${index + 1}`}
                >
                    <p>{`Limitation: ${index + 1}`}</p>
                    <div className="limitation">
                        Berth <span style={{ wordBreak: 'break-all', display: 'inline' }}>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].berth || ''}
                                newValue={newLim[index] && newLim[index].berth || null}
                            />
                        </span>
                    </div>
                    <div className="limitation">
                        Max draft <span>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].maxDraught || ''}
                                newValue={newLim[index] && newLim[index].maxDraught || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max beam <span>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].maxBeam || ''}
                                newValue={newLim[index] && newLim[index].maxBeam || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max LOA <span>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].maxLOA || ''}
                                newValue={newLim[index] && newLim[index].maxLOA || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Max DWT <span>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].maxDWT || ''}
                                newValue={newLim[index] && newLim[index].maxDWT || null}
                            /> m
                        </span>
                    </div>
                    <div className="limitation">
                        Channel depth <span>
                            <CheckDiffText
                                oldValue={oldLim[index] && oldLim[index].channelDepth || ''}
                                newValue={newLim[index] && newLim[index].channelDepth || null}
                            /> m
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
    };
    return (
        <React.Fragment>
            {(serviceProvider.status === 'new' || serviceProvider.status === 'normal') && renderNormalView()}
            {serviceProvider.status === 'remove' && renderRemoveView()}
            {serviceProvider.status === 'change' && renderChangeView()}
        </React.Fragment>
    );
};

StevedoringData.propTypes = {
    serviceProvider: PropTypes.shape({
        status: PropTypes.oneOf(['new', 'normal', 'remove', 'change']).isRequired,
        activities: PropTypes.arrayOf(PropTypes.string).isRequired,
        limitations: PropTypes.arrayOf(PropTypes.shape({
            berth: PropTypes.string,
            maxDraught: PropTypes.string,
            maxBeam: PropTypes.string,
            maxLOA: PropTypes.string,
            maxDWT: PropTypes.string,
            channelDepth: PropTypes.string,
        })).isRequired,
        provider: PropTypes.shape({
            activities: PropTypes.arrayOf(PropTypes.string),
            limitations: PropTypes.array,
        }),
    }).isRequired,
};

export default StevedoringData;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersApproval/StevedoringData.jsx